import { AlertTriangle } from 'lucide-react';

import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';

interface DeleteDocumentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isDeleting: boolean;
  selectedCount: number;
}

export function DeleteDocumentsDialog({
  open,
  onOpenChange,
  onConfirm,
  isDeleting,
  selectedCount,
}: DeleteDocumentsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--file-x-2 text-red-600" />
            Delete Documents
          </DialogTitle>
          <DialogDescription>
            <p>
              You are about to delete <strong>{selectedCount}</strong> {selectedCount === 1 ? 'document' : 'documents'}.
            </p>
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-red-300 bg-red-50 p-4">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <h3 className="text-sm font-medium text-red-800">Warning: Document Deletion</h3>
            </div>
            <div>
              <div className="mt-2 text-sm text-red-700">
                <p className="mb-2">
                  This action permanently removes all selected documents and their derived data from all subsets in this
                  matter. This deletion cannot be undone.
                </p>
                <ul className="ml-5 list-disc space-y-1 text-xs">
                  <li>Facts exclusively sourced from these documents will be permanently deleted</li>
                  <li>Kim threads containing these documents or their extracted facts will be removed</li>
                  <li>Inconsistencies related to deleted facts will be deleted</li>
                  <li>Entities related to deleted facts will be deleted</li>
                  <li>Chronologies will be updated to remove any facts extracted from deleted documents</li>
                  <li>
                    If any subset summary references deleted information, it will be temporarily unavailable and
                    regenerated. This may take several minutes.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button disabled={isDeleting}>Cancel</Button>
          </DialogClose>
          <Button variant="destructive" onClick={onConfirm} disabled={isDeleting}>
            {isDeleting ? (
              <>
                <Icon className="lucide--loader-2 h-4 w-4 animate-spin duration-700" />
                Deleting...
              </>
            ) : (
              <>
                <Icon className="lucide--file-x-2" />
                Confirm Delete
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
